import * as React from "react";
import { useNavigate } from "react-router";

import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import CloseIcon from "@mui/icons-material/Close";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";

import type {
    HomePromptDTO,
    HomePromptOption,
    RecommendedContent,
} from "@volley/data";

import SupportLink from "../../common/SupportLink";
import { useCurrentUser } from "../../hooks/currentUser";

import BrowseAllCard from "./BrowseAllCard";
import ContentResultCard, { contentNavUrl } from "./ContentResultCard";
import {
    fetchFreeTextRecommendations,
    fetchRecommendations,
    recordPromptEvent,
    submitProfileAnswer,
} from "./api";
import { saveChatSession } from "./chatSession";
import {
    BROWSE_ALL_VALUE,
    isAssessmentLaunchOption,
    isAptaConnectOption,
    isDuprConnectOption,
    isSeeStatsOption,
    WORKOUTS_SEARCH_ROUTE,
} from "./constants";
import type { ThreadMessage } from "./types";

const GREEN = "#3CE97C";
const NAVY_DEEP = "#0B1730";

/**
 * Cap on recommended content shown for an answer. Kept small so the carousel
 * stays a focused pick, not a full list — and so the recommendation snapshot
 * (what the server records as "served") matches what the user actually saw.
 */
const MAX_RECOMMENDATIONS = 4;

interface AskVolleyOverlayProps {
    prompt: HomePromptDTO;
    impressionUlid: string;
    /** The user's currently-selected sport; locks recommendations to it. */
    sportName?: string;
    /** Id of that same sport, recorded as the funnel dimension on every event. */
    sportId?: number | null;
    /** An option chosen in the widget, played automatically on open. */
    initialOptionValue?: string;
    /** Free text from the widget's open field; seeds the thread when present. */
    freeTextSeed?: string;
    /**
     * A previously-open chat to restore (after the user backed out of a workout
     * detail page). When present, the thread and remaining options are seeded
     * from it and the play-once effects are suppressed, so nothing is re-fetched.
     */
    restored?: {
        thread: ThreadMessage[];
        remainingOptions: HomePromptOption[];
    };
    onClose: () => void;
    /** Drive the assessment flow (start/resume) instead of fetching content. */
    onLaunchAssessment: (option: HomePromptOption) => void;
    /** Open the DUPR connect flow (profile prompt) instead of fetching content. */
    onConnectDupr: (option: HomePromptOption) => void;
    /** Open the APTA-id dialog (platform tennis's rating link) instead of content. */
    onConnectApta: (option: HomePromptOption) => void;
    /** Navigate to the performance dashboard ("see my stats") instead of content. */
    onSeeStats: (option: HomePromptOption) => void;
}

function Avatar({ size }: { size: number }): React.JSX.Element {
    return (
        <Box
            sx={{
                width: size,
                height: size,
                borderRadius: "50%",
                flexShrink: 0,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                color: NAVY_DEEP,
                background:
                    "radial-gradient(circle at 30% 30%, #58F08C 0%, #1FA557 60%, #0E6E3A 100%)",
            }}
        >
            <AutoAwesomeIcon sx={{ fontSize: size * 0.55 }} />
        </Box>
    );
}

/**
 * Fullscreen Ask Volley chat overlay. Seeds the thread with the prompt, lets the
 * user pick an answer, then shows the routed content recommendations (content
 * prompts) or acknowledges the answer (profile prompts).
 */
export default function AskVolleyOverlay({
    prompt,
    impressionUlid,
    sportName,
    sportId,
    initialOptionValue,
    freeTextSeed,
    restored,
    onClose,
    onLaunchAssessment,
    onConnectDupr,
    onConnectApta,
    onSeeStats,
}: AskVolleyOverlayProps): React.JSX.Element {
    const navigate = useNavigate();
    const { currentUser } = useCurrentUser();
    const userId = currentUser?.id;
    const [thread, setThread] = React.useState<ThreadMessage[]>(() =>
        // Restoring a chat the user left to view a workout: seed from the saved
        // thread verbatim (nothing is re-fetched).
        restored
            ? restored.thread
            : // When the user opened the chat from the open field, lead with their
              // own message + an acknowledgement; otherwise seed with the prompt.
              freeTextSeed
              ? [
                    { kind: "user", content: freeTextSeed },
                    {
                        kind: "assistant",
                        content: "Got it — let me pull that together for you.",
                    },
                ]
              : [{ kind: "assistant", content: prompt.promptText }],
    );
    const [remainingOptions, setRemainingOptions] = React.useState<
        HomePromptOption[]
    >(restored ? restored.remainingOptions : prompt.options);
    const [busy, setBusy] = React.useState(false);
    const [mounted, setMounted] = React.useState(false);
    const threadRef = React.useRef<HTMLDivElement>(null);

    // Once the conversation has turned into support — articles served, or a
    // support question we couldn't answer — the workout option chips are a non
    // sequitur ("Work on my dinks" under a trainer-connectivity answer), so the
    // footer goes away for the rest of the thread. An unroutable question or a
    // failed request keeps them: there the chips are a genuine way forward.
    const inSupportConversation = React.useMemo(
        () =>
            thread.some((m) =>
                m.kind === "carousel"
                    ? m.intent === "HELP"
                    : m.kind === "support" && m.origin === "help",
            ),
        [thread],
    );

    // Drive the slide-in on the next frame.
    React.useEffect(() => {
        const id = requestAnimationFrame(() => setMounted(true));
        return () => cancelAnimationFrame(id);
    }, []);

    // Keep the thread pinned to the latest message.
    React.useEffect(() => {
        if (threadRef.current) {
            threadRef.current.scrollTop = threadRef.current.scrollHeight;
        }
    }, [thread]);

    // Leave the chat for the full workouts library via the fixed trailing
    // carousel card, capturing the choice for attribution.
    const handleBrowseAll = React.useCallback(() => {
        recordPromptEvent({
            eventType: "interaction",
            prompt,
            impressionUlid,
            sportId,
            metadata: {
                action: "browse_all",
                answerValue: BROWSE_ALL_VALUE,
                answerLabel: "Browse all",
                via: "carousel",
            },
        });
        // Leaving via the trailing carousel card keeps the conversation going, so
        // persist it (like tapping a workout) — backing out of the library then
        // restores this exact thread and its recommendations.
        saveChatSession(userId, {
            savedAt: Date.now(),
            impressionUlid,
            sportId,
            prompt,
            initialOptionValue,
            freeTextSeed,
            thread,
            remainingOptions,
        });
        onClose();
        navigate(WORKOUTS_SEARCH_ROUTE);
    }, [
        impressionUlid,
        sportId,
        navigate,
        onClose,
        prompt,
        userId,
        initialOptionValue,
        freeTextSeed,
        thread,
        remainingOptions,
    ]);

    const handleSelect = React.useCallback(
        async (option: HomePromptOption) => {
            if (busy) return;

            // An assessment-launch option ("Start"/"Finish assessment") drives
            // the assessment flow directly rather than fetching content; the
            // parent records the interaction and resumes or opens the dialog.
            if (isAssessmentLaunchOption(option)) {
                onLaunchAssessment(option);
                return;
            }

            // The "Connect DUPR" option opens the DUPR connect flow directly
            // rather than fetching content; the parent records the interaction.
            if (isDuprConnectOption(option)) {
                onConnectDupr(option);
                return;
            }

            // Platform tennis's equivalent: link an APTA id so PTI resolves.
            if (isAptaConnectOption(option)) {
                onConnectApta(option);
                return;
            }

            // "See my stats" navigates to the performance dashboard rather than
            // fetching content; the parent records the interaction and navigates.
            if (isSeeStatsOption(option)) {
                onSeeStats(option);
                return;
            }

            // A dismiss option ("Maybe later") closes the chat instead of
            // routing or saving an answer; the choice is still captured.
            if (option.dismiss) {
                recordPromptEvent({
                    eventType: "interaction",
                    prompt,
                    impressionUlid,
                    sportId,
                    metadata: {
                        action: "dismiss",
                        answerValue: option.value,
                        answerLabel: option.label,
                    },
                });
                onClose();
                return;
            }

            setBusy(true);
            setRemainingOptions((prev) =>
                prev.filter((o) => o.value !== option.value),
            );
            recordPromptEvent({
                eventType: "interaction",
                prompt,
                impressionUlid,
                sportId,
                metadata: {
                    action: "answer_select",
                    answerValue: option.value,
                    answerLabel: option.label,
                },
            });
            setThread((prev) => [
                ...prev,
                { kind: "user", content: option.label },
            ]);

            if (prompt.promptType === "profile") {
                await submitProfileAnswer(
                    prompt.id,
                    { value: option.value },
                    impressionUlid,
                );
                setThread((prev) => [
                    ...prev,
                    {
                        kind: "assistant",
                        content:
                            "Got it — I'll use that to tune your recommendations.",
                    },
                ]);
                setBusy(false);
                return;
            }

            setThread((prev) => [...prev, { kind: "loading" }]);
            const cards = await fetchRecommendations(
                {
                    homePromptId: prompt.id,
                    answerValue: option.value,
                    impressionUlid,
                    ...(sportName ? { sportName } : {}),
                },
                MAX_RECOMMENDATIONS,
            );
            setThread((prev) => {
                const next = prev.filter((m) => m.kind !== "loading");
                if (cards.length === 0) {
                    return [
                        ...next,
                        {
                            kind: "assistant",
                            content:
                                "I couldn't find a match for that right now — try another option.",
                        },
                    ];
                }
                return [
                    ...next,
                    {
                        kind: "assistant",
                        content: "Here's what I'd start with:",
                    },
                    { kind: "carousel", cards },
                ];
            });
            setBusy(false);
        },
        [
            busy,
            impressionUlid,
            sportId,
            prompt,
            sportName,
            onClose,
            onLaunchAssessment,
            onConnectApta,
            onConnectDupr,
            onSeeStats,
        ],
    );

    // Play the option the user tapped in the widget, once.
    // When restoring, the thread is already populated — suppress both play-once
    // effects so the option/free-text recommendations aren't re-fetched.
    const initialPlayed = React.useRef(Boolean(restored));
    React.useEffect(() => {
        if (initialPlayed.current || !initialOptionValue) return;
        const option = prompt.options.find(
            (o) => o.value === initialOptionValue,
        );
        if (option) {
            initialPlayed.current = true;
            void handleSelect(option);
        }
    }, [initialOptionValue, prompt.options, handleSelect]);

    // Answer the free-text request the user typed in the widget, once: send it
    // to the LLM recommender and show the returned content (or a graceful miss).
    const freeTextPlayed = React.useRef(Boolean(restored));
    React.useEffect(() => {
        if (freeTextPlayed.current || !freeTextSeed) return;
        freeTextPlayed.current = true;
        void (async () => {
            setBusy(true);
            setThread((prev) => [...prev, { kind: "loading" }]);
            const result = await fetchFreeTextRecommendations(
                freeTextSeed,
                sportName,
                undefined,
                impressionUlid,
            );

            // Unroutable input: neither a training request nor a support
            // question. Offer a human rather than a dead end.
            if (result.kind === "not_workout_request") {
                recordPromptEvent({
                    eventType: "interaction",
                    prompt,
                    impressionUlid,
                    sportId,
                    metadata: {
                        action: "free_text_result",
                        outcome: "not_workout_request",
                        intent: "UNCLASSIFIED",
                        count: 0,
                    },
                });
                setThread((prev) => [
                    ...prev.filter((m) => m.kind !== "loading"),
                    {
                        kind: "support",
                        origin: "unroutable",
                        question: freeTextSeed,
                        // Only offer support help to users who can actually
                        // reach it. Outside the classifier rollout the server
                        // never routes to articles, so the wider promise would
                        // send them rephrasing towards a dead end.
                        content: result.helpAvailable
                            ? "I can help with your training or with using Volley — try rephrasing your question."
                            : "I can only help with workout recommendations right now — try asking about a skill, drill, or workout you'd like to train.",
                    },
                ]);
                setBusy(false);
                return;
            }

            // The request itself failed, so we never learned the intent. Say so
            // and offer a human: rendering the recommendation miss copy here
            // would tell someone who asked a support question to pick a workout
            // option, and never admit anything broke.
            if (result.kind === "error") {
                recordPromptEvent({
                    eventType: "interaction",
                    prompt,
                    impressionUlid,
                    sportId,
                    metadata: {
                        action: "free_text_result",
                        outcome: "error",
                        count: 0,
                    },
                });
                setThread((prev) => [
                    ...prev.filter((m) => m.kind !== "loading"),
                    {
                        kind: "support",
                        origin: "error",
                        question: freeTextSeed,
                        content:
                            "Something went wrong on my end — try asking again in a moment.",
                    },
                ]);
                setBusy(false);
                return;
            }

            const { cards, intent, summary } = result;
            recordPromptEvent({
                eventType: "interaction",
                prompt,
                impressionUlid,
                sportId,
                metadata: {
                    action: "free_text_result",
                    intent,
                    count: cards.length,
                    contentIds: cards.map((c) => c.id),
                },
            });
            setThread((prev) => {
                const next = prev.filter((m) => m.kind !== "loading");
                if (cards.length === 0) {
                    // A support question we have no article for is the one
                    // miss worth escalating: the user asked something
                    // answerable, we just don't hold the answer.
                    return intent === "HELP"
                        ? [
                              ...next,
                              {
                                  kind: "support",
                                  origin: "help",
                                  question: freeTextSeed,
                                  content:
                                      "I couldn't find an article on that.",
                              },
                          ]
                        : [
                              ...next,
                              {
                                  kind: "assistant",
                                  content:
                                      "I couldn't find a match for that right now — try rephrasing, or pick an option below.",
                              },
                          ];
                }
                // A help answer leads with the summary when we have one: it
                // answers the question in the thread, which is the whole point
                // of asking here rather than opening the help centre. The
                // generic lead-in is then dropped rather than stacked on top —
                // "here's what I found" reads as filler once a real answer sits
                // directly above the same cards.
                const lead: ThreadMessage =
                    intent === "HELP" && summary
                        ? { kind: "assistant", content: summary }
                        : {
                              kind: "assistant",
                              content:
                                  intent === "HELP"
                                      ? "Here's what I found that should help:"
                                      : "Here's what I'd recommend:",
                          };
                return [...next, lead, { kind: "carousel", cards, intent }];
            });
            setBusy(false);
        })();
    }, [freeTextSeed, sportName, prompt, impressionUlid, sportId]);

    const handleStart = React.useCallback(
        (content: RecommendedContent) => {
            recordPromptEvent({
                eventType: "interaction",
                prompt,
                impressionUlid,
                sportId,
                metadata: {
                    action: "content_tap",
                    contentId: content.id,
                    contentType: content.type,
                },
            });
            // Persist the open chat before leaving, so backing out of the workout
            // detail restores this exact thread (and its other recommendations).
            saveChatSession(userId, {
                savedAt: Date.now(),
                impressionUlid,
                sportId,
                prompt,
                initialOptionValue,
                freeTextSeed,
                thread,
                remainingOptions,
            });
            onClose();
            navigate(contentNavUrl(content), {
                state: { source: "ask_volley" },
            });
        },
        [
            impressionUlid,
            sportId,
            navigate,
            onClose,
            prompt,
            userId,
            initialOptionValue,
            freeTextSeed,
            thread,
            remainingOptions,
        ],
    );

    return (
        <Box
            sx={{
                position: "fixed",
                inset: 0,
                zIndex: (t) => t.zIndex.modal,
                bgcolor: NAVY_DEEP,
                display: "flex",
                flexDirection: "column",
                color: "#fff",
                transform: mounted ? "translateY(0)" : "translateY(20px)",
                opacity: mounted ? 1 : 0,
                transition:
                    "transform 260ms cubic-bezier(.2,.8,.2,1), opacity 220ms ease-out",
            }}
        >
            {/* Header */}
            <Stack
                direction="row"
                sx={{
                    alignItems: "center",
                    gap: 1.5,
                    px: 2,
                    py: 1.5,
                    flexShrink: 0,
                    borderBottom: "1px solid rgba(168,184,217,0.10)",
                }}
            >
                <Avatar size={40} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                        sx={{ fontWeight: 700, fontSize: 16, lineHeight: 1.1 }}
                    >
                        Volley
                    </Typography>
                    <Stack
                        direction="row"
                        sx={{ alignItems: "center", gap: 0.75 }}
                    >
                        <Box
                            sx={{
                                width: 6,
                                height: 6,
                                borderRadius: "50%",
                                bgcolor: GREEN,
                                boxShadow: `0 0 6px ${GREEN}`,
                            }}
                        />
                        <Typography
                            sx={{ fontSize: 12, color: GREEN, fontWeight: 500 }}
                        >
                            Picked for you
                        </Typography>
                    </Stack>
                </Box>
                <IconButton
                    onClick={onClose}
                    aria-label="close"
                    sx={{ color: "#fff", bgcolor: "rgba(255,255,255,0.06)" }}
                >
                    <CloseIcon sx={{ fontSize: 16 }} />
                </IconButton>
            </Stack>

            {/* Thread */}
            <Box
                ref={threadRef}
                sx={{
                    flex: 1,
                    minHeight: 0,
                    overflowY: "auto",
                    overflowX: "hidden",
                    py: 2,
                }}
            >
                {thread.map((message, i) => {
                    const key = `${message.kind}-${i}`;
                    if (message.kind === "assistant") {
                        return (
                            <Stack
                                key={key}
                                direction="row"
                                sx={{
                                    gap: 1,
                                    px: 2,
                                    mb: 1.75,
                                    alignItems: "flex-end",
                                }}
                            >
                                <Avatar size={28} />
                                <Box
                                    sx={{
                                        maxWidth: "78%",
                                        px: 1.75,
                                        py: 1.5,
                                        borderRadius: 2,
                                        borderBottomLeftRadius: 6,
                                        bgcolor: "#182B57",
                                        fontSize: 15,
                                        lineHeight: 1.4,
                                    }}
                                >
                                    {message.content}
                                </Box>
                            </Stack>
                        );
                    }
                    if (message.kind === "support") {
                        return (
                            <Stack
                                key={key}
                                direction="row"
                                sx={{
                                    gap: 1,
                                    px: 2,
                                    mb: 1.75,
                                    alignItems: "flex-end",
                                }}
                            >
                                <Avatar size={28} />
                                <Box
                                    sx={{
                                        maxWidth: "78%",
                                        px: 1.75,
                                        py: 1.5,
                                        borderRadius: 2,
                                        borderBottomLeftRadius: 6,
                                        bgcolor: "#182B57",
                                        fontSize: 15,
                                        lineHeight: 1.4,
                                    }}
                                >
                                    {message.content}
                                    <SupportLink
                                        // Hand the messenger what they already
                                        // typed: this is the one path where we
                                        // know the question and are admitting we
                                        // can't answer it.
                                        message={message.question}
                                        onEscalate={() =>
                                            recordPromptEvent({
                                                eventType: "interaction",
                                                prompt,
                                                impressionUlid,
                                                sportId,
                                                metadata: {
                                                    action: "support_escalate",
                                                    source: "widget",
                                                    origin: message.origin,
                                                },
                                            })
                                        }
                                        TypographyProps={{
                                            align: "left",
                                            sx: { mt: 1, mb: 0 },
                                        }}
                                        sx={{ p: 0, minWidth: 0 }}
                                    >
                                        Message support
                                    </SupportLink>
                                </Box>
                            </Stack>
                        );
                    }
                    if (message.kind === "user") {
                        return (
                            <Stack
                                key={key}
                                direction="row"
                                sx={{
                                    px: 2,
                                    mb: 1.75,
                                    justifyContent: "flex-end",
                                }}
                            >
                                <Box
                                    sx={{
                                        maxWidth: "78%",
                                        px: 1.75,
                                        py: 1.5,
                                        borderRadius: 2,
                                        borderBottomRightRadius: 6,
                                        bgcolor: "#fff",
                                        color: NAVY_DEEP,
                                        fontWeight: 500,
                                        fontSize: 15,
                                        lineHeight: 1.4,
                                    }}
                                >
                                    {message.content}
                                </Box>
                            </Stack>
                        );
                    }
                    if (message.kind === "loading") {
                        return (
                            <Stack
                                key={key}
                                direction="row"
                                sx={{
                                    gap: 1,
                                    px: 2,
                                    mb: 1.75,
                                    alignItems: "center",
                                }}
                            >
                                <Avatar size={28} />
                                <CircularProgress
                                    size={18}
                                    sx={{ color: GREEN }}
                                />
                            </Stack>
                        );
                    }
                    // carousel
                    return (
                        <Stack
                            key={key}
                            direction="row"
                            sx={{
                                gap: 1.5,
                                px: 2,
                                pb: 1.5,
                                mb: 0.5,
                                overflowX: "auto",
                                scrollSnapType: "x proximity",
                                "&::-webkit-scrollbar": { display: "none" },
                                scrollbarWidth: "none",
                            }}
                        >
                            {message.cards.map((card) => (
                                <ContentResultCard
                                    key={`${card.type}-${card.id}`}
                                    content={card}
                                    onStart={handleStart}
                                />
                            ))}
                            {/* Trailing "browse all workouts" card — offered
                                after workout recommendations, but not after
                                support articles, where routing someone to the
                                workout library is a non sequitur. */}
                            {message.intent !== "HELP" && (
                                <BrowseAllCard onBrowseAll={handleBrowseAll} />
                            )}
                        </Stack>
                    );
                })}
            </Box>

            {/* Footer: remaining answer options */}
            {remainingOptions.length > 0 && !inSupportConversation && (
                <Stack
                    direction="row"
                    sx={{
                        gap: 1,
                        px: 2,
                        py: 1.5,
                        flexShrink: 0,
                        overflowX: "auto",
                        borderTop: "1px solid rgba(168,184,217,0.10)",
                        "&::-webkit-scrollbar": { display: "none" },
                        scrollbarWidth: "none",
                    }}
                >
                    {remainingOptions.map((option) => (
                        <Box
                            key={option.value}
                            component="button"
                            disabled={busy}
                            onClick={() => void handleSelect(option)}
                            sx={{
                                display: "inline-flex",
                                alignItems: "center",
                                px: 1.75,
                                py: 1,
                                bgcolor: "transparent",
                                border: "1px solid rgba(168,184,217,0.25)",
                                borderRadius: 999,
                                color: "#E2E8F0",
                                fontFamily: "inherit",
                                fontSize: 13,
                                fontWeight: 500,
                                whiteSpace: "nowrap",
                                flexShrink: 0,
                                cursor: busy ? "default" : "pointer",
                                opacity: busy ? 0.5 : 1,
                                "&:hover": {
                                    borderColor: "rgba(60,233,124,0.5)",
                                },
                            }}
                        >
                            {option.label}
                        </Box>
                    ))}
                </Stack>
            )}
        </Box>
    );
}
